<template>
    <div>
        <div v-if="attachments && attachments.length && canRead">
            <ul class="list-unstyled">
                <li
                    v-for="attachment in attachments"
                    :key="attachment.id"
                    :class="{ 'border-0': inline }"
                    class="border-color-secondary mb-2 flex rounded border"
                >
                    <div v-if="!inline" class="items-top flex p-2 text-primary">
                        <image-file-type
                            v-if="attachment.mimeType.startsWith('image')"
                            :style="isDesktop ? 'height:22px; width:22px' : 'height:16px; width:16px'"
                        />
                        <pdf-file-type v-else />
                    </div>
                    <div class="d-sm-flex ps-sm-2 grow justify-between p-2 ps-0">
                        <div class="flex flex-col justify-center" style="line-height: normal">
                            <div class="flex items-start" style="gap: 5px">
                                <a :href="attachment.uri" class="text-break line-clamp-2" target="_blank">{{ attachment.fileName }} </a>
                                <icon
                                    v-if="!attachment.exists"
                                    v-c-tooltip="{
                                        content: trans('general.attachment.not_found'),
                                        popperOptions: {
                                            positionFixed: true,
                                        },
                                    }"
                                    icon="cil-warning"
                                />
                            </div>
                            <small class="text-nowrap" style="line-height: 1.5">
                                {{ trans('attachments.overview.size') }}:
                                {{ $filters.prettyBytes(attachment.size, currentLocale) }}
                                &nbsp;&nbsp;&nbsp;{{ $filters.dateTimeMedium(attachment.createdAt, currentLocale) }}
                            </small>
                        </div>
                        <div v-if="!readonly" class="actions flex items-center justify-end gap-1">
                            <div v-if="canRead">
                                <a
                                    v-c-tooltip="{
                                        content: trans('general.action.download'),
                                    }"
                                    :href="attachment.downloadUrl"
                                    class="btn btn-icon text-primary"
                                >
                                    <icon icon="cil-cloud-download" />
                                </a>
                            </div>
                            <div v-if="canEdit">
                                <jet-button
                                    v-c-tooltip="{
                                        content: trans('general.action.edit'),
                                    }"
                                    class="btn-icon"
                                    type="button"
                                    variant="tertiary"
                                    @click.prevent="editFilename(attachment)"
                                >
                                    <icon icon="cil-pencil" />
                                </jet-button>
                            </div>
                            <div v-if="canDelete">
                                <jet-button
                                    v-c-tooltip="{
                                        content: trans('general.action.delete'),
                                    }"
                                    :destructive="true"
                                    :is-processing="isDeletingAttachment(attachment)"
                                    class="btn-icon"
                                    type="button"
                                    variant="tertiary"
                                    @click.prevent="deleteAttachment(attachment)"
                                >
                                    <icon icon="cil-trash" />
                                </jet-button>
                            </div>
                        </div>
                    </div>
                </li>
            </ul>
        </div>
        <dialog-modal :closeable="false" :show="!!selectedAttachment" @close="closeModal">
            <template #title>
                {{ trans('attachments.modal.update_file_name.header') }}
            </template>

            <div class="mt-2">
                <jet-input
                    ref="filename"
                    v-model="form.name"
                    :autofocus="true"
                    :horizontal="false"
                    :invalidFeedback="form.errors?.name?.[0]"
                    :label="trans('attachments.modal.update_file_name.label')"
                    :placeholder="trans('attachments.modal.update_file_name.placeholder')"
                    class="mt-1 block w-3/4"
                    type="text"
                    @keyup.enter.prevent="submitForm"
                >
                    <template #append>
                        <span v-if="selectedAttachment"> .{{ selectedAttachment.extension }} </span>
                    </template>
                </jet-input>
            </div>

            <template #footer>
                <jet-button :disabled="form.processing" type="button" variant="tertiary" @click.prevent="closeModal">
                    {{ trans('general.action.cancel') }}
                </jet-button>

                <jet-button :disabled="!isFormSubmittable" :is-processing="form.processing" type="button" @click.prevent="submitForm">
                    {{ trans('general.action.save') }}
                </jet-button>
            </template>
        </dialog-modal>
    </div>
</template>
<script setup>
import JetButton from '@/Jetstream/Button.vue';
import JetInput from '@/Jetstream/Input.vue';
import DialogModal from '@/Jetstream/DialogModal.vue';
import ImageFileType from '../../icons/ImageFileType.vue';
import PdfFileType from '../../icons/PdfFileType.vue';
import { $http } from '@/bootstrap.js';
import { isDesktop } from '@/Utils/UseMedia';
import { trans } from '../mixins/i18n.js';
import { useForm } from '@inertiajs/vue3';
import { computed, ref } from 'vue';

const emit = defineEmits(['deleted', 'updated']);

defineProps({
    readonly: {
        type: Boolean,
        default: false,
    },
    attachments: Array,
    inline: {
        type: Boolean,
        default: false,
    },
    canDelete: {
        type: Boolean,
        default: false,
    },
    canEdit: {
        type: Boolean,
        default: false,
    },
    canRead: {
        type: Boolean,
        default: true,
    },
});

const form = useForm({
    name: null,
});

const isFormSubmittable = computed(() => !form.processing && form.isDirty);

const selectedAttachment = ref(null);
const isDeleting = ref([]);
const filename = ref(null);

function editFilename(attachment) {
    form.defaults({ name: attachment.basename }).resetAndClearErrors();
    selectedAttachment.value = attachment;
}

function submitForm() {
    const id = selectedAttachment.value.id;

    if (form.processing) {
        return;
    }

    form.processing = true;

    $http
        .put(route('attachments.update', id), form.data(), {})
        .then((resp) => {
            emit('updated', resp.data);
            closeModal();
        })
        .catch((resp) => {
            form.errors = resp.response.data?.errors;

            if (filename.value && typeof filename.value.focus === 'function') {
                filename.value.focus();
            }
        })
        .finally(() => {
            form.processing = false;
        });
}

function deleteAttachment(attachment) {
    isDeleting.value = [...isDeleting.value, attachment.id];

    $http
        .delete(route('attachments.destroy', attachment.id))
        .then(() => {
            emit('deleted', attachment);
        })
        .finally(() => {
            isDeleting.value = isDeleting.value.filter((id) => id !== attachment.id);
        });
}

function closeModal() {
    selectedAttachment.value = null;
}

function isDeletingAttachment(attachment) {
    return isDeleting.value.includes(attachment.id);
}
</script>
<style scoped>
.media {
    width: 100px;
    height: 100%;
}

:deep(.input-group-text) {
    min-width: 32px;
    width: auto;
}
</style>
